import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { 
  LayoutDashboard, 
  Link as LinkIcon, 
  TrendingUp, 
  CreditCard, 
  User, 
  HelpCircle, 
  ShoppingCart,
  GraduationCap,
  Users,
  UserCircle,
  FileText,
  DollarSign,
  Hash,
  UsersRound,
  BarChart3,
  MapPin,
  Building2,
  LinkIcon as SecondaryLinkIcon,
  Share2,
  ChevronDown,
  Star,
  X,
  LogOut,
  Info,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { userService } from "../../_services";
import { cn } from "@/lib/utils";
import GoPro from "../Consumer/GoPro";
import GoProConfirmation from "../Consumer/GoProConfirmation";

// Commission Tier Popup Component — matches dev-v3 CtPopup with full tier table
function CommissionTierPopup({ isOpen, onClose, tierData, tierInfo, percentage }) {
  const [showPopupTooltip, setShowPopupTooltip] = React.useState(false);

  if (!tierInfo || !tierData) return null;

  // tierJson may be a string (from API) or already parsed array
  let allTiers = [];
  try {
    allTiers = typeof tierInfo.tierJson === 'string'
      ? JSON.parse(tierInfo.tierJson)
      : (tierInfo.tierJson || []);
  } catch { allTiers = []; }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>NuLifeSpan Rx RockStar Revenue Program</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Current tier progress card — same grey card as sidebar */}
          <div className="bg-gray-100 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              {tierData.tierIcon && (
                <img src={tierData.tierIcon} alt="" className="w-6 h-6 rounded-full" />
              )}
              <span className="text-sm font-semibold text-primary">{tierInfo.tierName}</span>
            </div>
            <div className="space-y-1">
              {/* Progress bar — tooltip shown strictly on progress bar hover */}
              <div className="relative pt-6">
                {showPopupTooltip && (
                  <div
                    className="absolute top-0 text-xs bg-card border border-border rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap pointer-events-none z-10"
                    style={{ left: `calc(${Math.min(percentage, 90)}% - 12px)` }}
                  >
                    {tierInfo.claims} Transaction{tierInfo.claims !== 1 ? 's' : ''}
                  </div>
                )}
                <div
                  onMouseEnter={() => setShowPopupTooltip(true)}
                  onMouseLeave={() => setShowPopupTooltip(false)}
                >
                  <Progress value={percentage} className="h-2" />
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{tierData.from}</span>
                <span>{tierData.to}</span>
              </div>
            </div>
            <p className="text-xs font-semibold text-primary">
              {Math.max(tierData.to - tierInfo.claims + 1, 0)} Transaction{(tierData.to - tierInfo.claims + 1) !== 1 ? 's' : ''} to reach the next level
            </p>
          </div>

          {/* Description */}
          <p className="text-sm">
            The NuLifeSpan Rx RockStar Revenue Program gives you the opportunity to scale up your commission earnings as you produce more claims! The table below provides all of the details.
          </p>

          {/* How it works table — matches dev-v3 */}
          {allTiers.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">How it works</h3>
              <div className="border rounded-md overflow-hidden">
                {/* Sticky header */}
                <div className="grid grid-cols-3 bg-muted px-3 py-2 text-xs font-semibold text-muted-foreground uppercase sticky top-0 z-10">
                  <div>Tier Name</div>
                  <div>No. of Transactions</div>
                  <div>Commission ($)</div>
                </div>
                {/* Scrollable body */}
                <div className="max-h-48 overflow-y-auto">
                  {allTiers.map((tdata, i) => (
                    <div
                      key={i}
                      className={`grid grid-cols-3 px-3 py-2 text-sm ${i % 2 === 0 ? 'bg-card' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-1">
                        {tdata.tierIcon && <img src={tdata.tierIcon} alt="" className="w-4 h-4" />}
                        {tdata.tierName}
                      </div>
                      <div>{tdata.from} - {tdata.to}</div>
                      <div>${tdata.commission}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AppSidebar({ isAdmin, isConsumer }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpenMobile, setOpen, state, isMobile } = useSidebar();
  
  const [commissionTier, setCommissionTier] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [percentage, setPercentage] = useState(0);
  const [showTraining, setShowTraining] = useState(false);
  const [showTierPopup, setShowTierPopup] = useState(false);
  const [showGoProModal, setShowGoProModal] = useState(false);
  const [showGoProConfirmation, setShowGoProConfirmation] = useState(false);
  const [showProgressTooltip, setShowProgressTooltip] = useState(false);

  let fullName = '';
  try {
    const raw = localStorage.getItem('hacker_user_detail');
    if (raw) fullName = window.atob(raw).split(':')[1] || '';
  } catch (e) {
    // localStorage value not yet set or invalid — fullName stays empty
  }
  const isDistributor = !isAdmin && !isConsumer;

  // Fetch commission tier data for distributors (matches dev-v3: commissionTierInfo)
  useEffect(() => {
    if (!isDistributor) return;
    try {
      const userId = window.atob(localStorage.getItem('ajs_user_id')).split(':')[0];
      userService.Get_UserTierProperties(userId).then((userdata) => {
        if (userdata && userdata.length > 0) {
          // tierJson may come as a JSON string from the API — parse if needed
          let tierJson = userdata[0].tierJson;
          if (typeof tierJson === 'string') {
            try { tierJson = JSON.parse(tierJson); } catch { tierJson = []; }
          }

          const filteredCtData = (tierJson || []).filter(
            (td) => td.tierName === userdata[0].tierName
          );

          if (filteredCtData.length > 0) {
            const ctData = filteredCtData[0];
            // Store the parsed tierJson back so the popup can use it
            const ctInfo = { ...userdata[0], tierJson };
            setCommissionTier(ctInfo);
            setTierData(ctData);
            // calc percentage (matches dev-v3 calcPercentage)
            try {
              const total = ctData.to - ctData.from;
              const value = ctInfo.claims - ctData.from;
              setPercentage(Math.min(Math.floor((value / total) * 100), 100));
            } catch {
              setPercentage(100);
            }
          }
        }
      });
    } catch (e) {
      // localStorage not ready or invalid
    }
  }, [isDistributor]);

  const handleNavClick = () => {
    setOpenMobile(false);
  };

  const handleSubmenuTriggerClick = () => {
    // Only expand sidebar if it's collapsed when clicking on Analytics submenu
    if (state === 'collapsed') {
      setOpen(true);
    }
  };

  const openGoProModal = () => {
    setShowGoProModal(true);
  };

  const closeGoProModal = () => {
    setShowGoProModal(false);
  };

  const openConfirmationModal = () => {
    setShowGoProModal(false);
    setShowGoProConfirmation(true);
  };

  const closeConfirmationModal = () => {
    setShowGoProConfirmation(false);
  };

  const handleLogout = () => {
    userService.logout();
    navigate('/signup');
  };

  // Admin navigation items
  const adminNavItems = [
    { path: '/Admin/groups', label: 'Group Numbers', icon: UsersRound },
    { path: '/Admin/training', label: 'Training', icon: GraduationCap },
    { path: '/Admin/users', label: 'Users', icon: UserCircle },
    { path: '/Admin/report', label: 'Reports', icon: FileText },
    { path: '/Admin/transactions', label: 'Transactions', icon: TrendingUp },
    { path: '/Admin/pricing', label: 'Pricing Tool', icon: DollarSign },
    { path: '/Admin/referral', label: 'Referral Links', icon: Share2 },
    { path: '/Admin/secondary-link', label: 'Secondary Link', icon: SecondaryLinkIcon },
    { path: '/account', label: 'Account Info', icon: User },
  ];

  const analyticsItems = [
    { path: '/Admin/analytics/geographic', label: 'Geographic', icon: MapPin },
    { path: '/Admin/analytics/pharmacies', label: 'Pharmacies', icon: Building2 },
  ];

  // User/Distributor navigation items
  const userNavItems = [
    { path: '/Summary', label: 'Summary', icon: LayoutDashboard, show: isDistributor },
    { path: '/training', label: 'Training', icon: GraduationCap, show: isDistributor },
    { path: '/ref', label: 'Affiliate Info', icon: LinkIcon, show: isDistributor },
    { path: '/dashboard', label: 'Claims Dashboard', icon: TrendingUp, show: isDistributor },
    { path: '/cards', label: 'Marketing Materials', icon: CreditCard, show: isDistributor },
    { path: '/nls-rx-marketplace', label: 'NLS Rx Marketplace', icon: ShoppingCart, show: isDistributor },
    { path: '/account', label: 'Account Info', icon: User, show: true },
    { path: '/faq', label: "FAQ's", icon: HelpCircle, show: true },
  ];

  // Consumer navigation items
  const consumerNavItems = [
    { path: '/consumer/Summary', label: 'Summary', icon: LayoutDashboard },
    { path: '/consumer/card', label: 'Share Card', icon: Share2 },
    { path: '/consumer/faq', label: "Cashback FAQ's", icon: HelpCircle },
    { path: '/consumer/training', label: 'Training', icon: GraduationCap },
  ];

  const navItems = isAdmin ? adminNavItems : isConsumer ? consumerNavItems : userNavItems.filter(item => item.show);

  return (
    <>
      <Sidebar collapsible="icon" className="border-r border-border pt-16">
        {/* Mobile close button */}
        {isMobile && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-muted">
            <span className="text-sm font-semibold text-primary">Menu</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary hover:bg-sidebar-accent hover:text-accent"
              onClick={() => setOpenMobile(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Commission Tier Section for Distributors — matches dev-v3 commision_tier box */}
        {commissionTier && tierData && isDistributor && (
          <SidebarGroup className="px-3 pt-4 pb-2">
            {state === 'collapsed' ? (
              /* Collapsed: icon + total transaction count */
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="w-full flex flex-col items-center gap-1 bg-gray-100 rounded-lg p-2 hover:bg-gray-200 transition-colors cursor-pointer"
                    onClick={() => setShowTierPopup(true)}
                  >
                    <TrendingUp className="h-5 w-5 text-accent shrink-0" />
                    <span className="text-xs font-bold text-primary leading-none">{commissionTier.claims}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {commissionTier.claims} Transaction{commissionTier.claims !== 1 ? 's' : ''} — {commissionTier.tierName}
                </TooltipContent>
              </Tooltip>
            ) : (
              /* Expanded: full tier card */
              <div className="bg-gray-100 rounded-lg p-4 space-y-3">
                {/* Title row: tier icon + name + info button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {tierData.tierIcon ? (
                      <img src={tierData.tierIcon} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                        <Star className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span className="text-sm font-semibold text-primary">{commissionTier.tierName}</span>
                  </div>
                  <button
                    className="text-primary hover:text-accent cursor-pointer"
                    onClick={() => setShowTierPopup(true)}
                    title="View tier details"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>

                {/* Progress bar — tooltip shown strictly on progress bar hover */}
                <div className="relative pt-6">
                  {showProgressTooltip && (
                    <div
                      className="absolute top-0 text-xs bg-card border border-border rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap pointer-events-none z-10"
                      style={{ left: `calc(${Math.min(percentage, 90)}% - 12px)` }}
                    >
                      {commissionTier.claims} Transaction{commissionTier.claims !== 1 ? 's' : ''}
                    </div>
                  )}
                  <div
                    onMouseEnter={() => setShowProgressTooltip(true)}
                    onMouseLeave={() => setShowProgressTooltip(false)}
                  >
                    <Progress value={percentage} className="h-2" />
                  </div>
                </div>

                {/* Min / Max labels */}
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{tierData.from}</span>
                  <span>{tierData.to}</span>
                </div>

                {/* "X Transactions to reach next level" */}
                <p className="text-xs font-semibold text-primary">
                  {Math.max(tierData.to - commissionTier.claims + 1, 0)} Transaction{(tierData.to - commissionTier.claims + 1) !== 1 ? 's' : ''} to reach the next level
                </p>
              </div>
            )}
          </SidebarGroup>
        )}

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = location.pathname.toLowerCase() === item.path.toLowerCase();
                  
                  return (
                    <React.Fragment key={item.path}>
                      <SidebarMenuItem>
                        <SidebarMenuButton 
                          asChild 
                          isActive={isActive}
                          tooltip={item.label}
                          onClick={handleNavClick}
                          className={cn(
                            "group p-2.5 text-base font-medium transition-all duration-200",
                            isActive 
                              ? 'text-accent bg-sidebar-accent' 
                              : 'text-primary bg-transparent hover:bg-sidebar-accent hover:text-accent'
                          )}
                        >
                          <Link to={item.path} className="flex items-center gap-3">
                            <Icon className="h-7 w-7 transition-colors duration-200" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      
                      {/* Analytics submenu appears right after Transactions */}
                      {isAdmin && item.path === '/Admin/transactions' && (
                        <Collapsible defaultOpen={false}>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                    onClick={handleSubmenuTriggerClick}
                                    tooltip="Analytics"
                                    className="group cursor-pointer hover:bg-sidebar-accent hover:text-accent rounded-md flex items-center justify-between text-primary text-base font-medium p-2.5 w-full transition-all duration-200"
                                  >
                                <div className="flex items-center gap-3">
                                  <BarChart3 className="h-5 w-5 transition-colors duration-200" />
                                  <span>Analytics</span>
                                </div>
                                <ChevronDown className="h-3.5 w-3.5 transition-all duration-200 group-data-[state=open]:rotate-180" />
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub className="space-y-1">
                              {analyticsItems.map((subItem) => {
                                const SubIcon = subItem.icon;
                                const isSubActive = location.pathname.toLowerCase() === subItem.path.toLowerCase();
                                
                                return (
                                  <SidebarMenuSubItem key={subItem.path}>
                                    <SidebarMenuSubButton 
                                      asChild 
                                      isActive={isSubActive}
                                      onClick={handleNavClick}
                                      className={cn(
                                        "group p-2 text-[15px] font-medium transition-all duration-200",
                                        isSubActive 
                                          ? 'text-accent bg-sidebar-accent' 
                                          : 'text-primary bg-transparent hover:bg-sidebar-accent hover:text-accent'
                                      )}
                                    >
                                      <Link to={subItem.path} className="flex items-center gap-3">
                                        <SubIcon className="h-7 w-7 transition-colors duration-200" />
                                        <span>{subItem.label}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </React.Fragment>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

        </SidebarContent>

        {/* Go Pro Button for Consumers - pinned to sidebar bottom */}
        {isConsumer && (
          <SidebarFooter className="border-t border-muted p-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={openGoProModal}
                  className={cn(
                    "w-full bg-accent hover:bg-accent text-white hover:text-white font-semibold py-2.5 rounded-md flex items-center justify-center gap-2",
                    state === 'collapsed' && "px-2"
                  )}
                >
                  <Star className="h-5 w-5 fill-white shrink-0" />
                  {state !== 'collapsed' && <span>Go Pro</span>}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" hidden={state !== 'collapsed'}>
                Go Pro
              </TooltipContent>
            </Tooltip>
          </SidebarFooter>
        )}

        {/* Logout for Admin & Distributor - pinned to sidebar bottom */}
        {!isConsumer && (
          <SidebarFooter className="border-t border-muted p-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className={cn(
                    "w-full flex items-center gap-3 text-primary hover:bg-sidebar-accent hover:text-accent font-medium text-base p-2.5 h-auto justify-start",
                    state === 'collapsed' && "justify-center px-2"
                  )}
                >
                  <LogOut className="h-7 w-7 shrink-0" />
                  {state !== 'collapsed' && <span>Logout</span>}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" hidden={state !== 'collapsed'}>
                Logout
              </TooltipContent>
            </Tooltip>
          </SidebarFooter>
        )}

        
      </Sidebar>

      {/* Commission Tier Popup */}
      <CommissionTierPopup
        isOpen={showTierPopup}
        onClose={() => setShowTierPopup(false)}
        tierData={tierData}
        tierInfo={commissionTier}
        percentage={percentage}
      />

      {/* Go Pro Modal */}
      {showGoProModal && (
        <GoPro 
          open={showGoProModal} 
          onClose={closeGoProModal}
          openConfirmationModal={openConfirmationModal}
        />
      )}

      {/* Go Pro Confirmation Modal */}
      {showGoProConfirmation && (
        <GoProConfirmation 
          open={showGoProConfirmation} 
          onClose={closeConfirmationModal}
          openGoProModal={openGoProModal}
          logout={handleLogout}
        />
      )}
    </>
  );
}

